import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGet, secureDelete, securePut } from '../../config/api';
import { PrimaryButton } from '../../components/PrimaryButton';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import FieldForm from './FieldForm';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import TypeForm from './TypeForm';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import EyeIcon from '../../icons/eye.svg';
import StarIcon from '../../icons/settings.svg';
import DeleteIcon from '../../icons/trashR.svg';

const ExtraFields = () => {
  const [filters, setFilters] = useState({
    needle: "",
    type: "",
    required: "",
    linkedTo: "",
  });
  const [fields, setFields] = useState([]);
  const [filteredFields, setFilteredFields] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [, setSelectedField] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [showTypeForm, setShowTypeForm] = useState(false);

  const { t } = useTranslation();

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "type",
      label: "Type",
      type: "dropdown",
      options: types.map((type) => ({ value: type.name, label: type.name })),
    },
    {
      name: "required",
      label: t(`filters.config.required`),
      type: "dropdown",
      options: [
        { value: "true", label: t(`required_status.true`) },
        { value: "false", label: t(`required_status.false`) },
      ],
    },
    {
      name: "linkedTo",
      label: t(`filters.config.linkedTo`),
      type: "dropdown",
      options: [
        { value: "cart", label: "Panier" },
        { value: "product", label: "Produit" },
        { value: "client", label: "Client" },
        { value: "brand", label: "Marque" },
        { value: "family", label: "Famille" },
        { value: "provider", label: "Fournisseur" },
        { value: "purchase", label: "Achat" },
      ],
    },
  ];

  const fetchFields = async (module) => {
    
    if (!module || typeof module !== 'string' || module.trim() === '') {
      console.warn('fetchFields called with invalid module:', module);
      setFields([]);
      setFilteredFields([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await secureGet(`/config/${module}/fields`, showSnackbar, { signal });
      const data = Array.isArray(response.data) ? response.data : [];
      setFields(data);
      setFilteredFields(data);
      setTotalPages(Math.ceil(data.length / pageSize));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching fields:', error);
      }
      setFields([]);
      setFilteredFields([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchTypes = async () => {
    try {
      const response = await secureGet('/config/types', showSnackbar, { signal });
      setTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching types:', error);
      }
      setTypes([]);
    }
  };
  useEffect(() => {
    fetchTypes();

    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);


  const applyFilters = useCallback(async (data = fields) => {
    let filteredData = [...data];

    if (filters.needle) {
      const searchTerm = filters.needle.toLowerCase();
      filteredData = filteredData.filter((field) =>
        field.name.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.type) {
      filteredData = filteredData.filter((field) => field.type?.name === filters.type);
    }

    if (filters.required) {
      const isMandatory = filters.required === "true";
      filteredData = filteredData.filter((field) => field.mandatory === isMandatory);
    }


    setFilteredFields(filteredData);
    setTotalPages(Math.ceil(filteredData.length / pageSize));
    setPageNumber(0);
  }, [filters, fields, pageSize]);

  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: filterValue,
    }));

    if (filterName === "linkedTo") {
      setFields([]);
      if (filterValue) fetchFields(filterValue);
    }
  };

  const resetFilters = () => {
    setFilters({
      needle: "",
      type: "",
      required: "",
      linkedTo: "",
    });
    setPageNumber(0);
    setFields([]);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    if (!filters.linkedTo || filters.linkedTo.trim() === '') {
      console.error('Cannot delete: linkedTo filter is empty');
      showSnackbar('Error: No module selected', 'error');
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      return;
    }

    const moduleMapping = {
      cart: "cart",
      product: "product",
      client: "client",
      brand: "brand",
      family: "family",
      provider: "provider",
      purchase: "purchase",
    };

    const module = moduleMapping[filters.linkedTo.toLowerCase()];

    if (!module) {
      console.error('Invalid module for deletion:', filters.linkedTo);
      showSnackbar('Error: Invalid module', 'error');
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      return;
    }

    try {
      const deleteUrl = `/config/${module}/fields/delete/${itemToDelete.id}`;
      const repsonce = await secureDelete(deleteUrl, showSnackbar);
      showSnackbar(t(`confirmation.configDelete`), 'success');

      fetchFields(filters.linkedTo.toLowerCase());
    } catch (error) {
      console.error('Error deleting field:', error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleAddClick = () => {
    setShowModule(true);
  };

  const handleAddTypeClick = () => {
    setShowTypeForm(true);
  };

  const handleModuleClose = () => {
    setShowModule(false);
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
    }
  };
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTotalPages(Math.ceil(filteredFields.length / newPageSize));
    setPageNumber(0);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, fields, applyFilters]);

  const handleRequiredChange = async (field) => {
    if (!filters.linkedTo || filters.linkedTo.trim() === '') {
      console.error('Cannot update field: linkedTo filter is empty');
      showSnackbar('Error: No module selected', 'error');
      return;
    }

    const moduleMapping = {
      cart: "cart",
      product: "product",
      client: "client",
      brand: "brand",
      family: "family",
      provider: "provider",
      purchase: "purchase",
    };

    const module = moduleMapping[filters.linkedTo.toLowerCase()];

    if (!module) {
      console.error('Invalid module for field update:', filters.linkedTo);
      showSnackbar('Error: Invalid module', 'error');
      return;
    }

    const payload = {
      id: field.id,
      name: field.name,
      type: field.type?.id,
      visible: field.visible,
      mandatory: !field.mandatory,
    };


    try {
      await securePut(`/config/${module}/fields/add`, payload, showSnackbar);
      fetchFields(filters.linkedTo.toLowerCase());
    } catch (error) {
      console.error("Error submitting the form:", error);
    }
  };

  const getMenuOptions = (field) => [
    {
      label: field.mandatory ? t(`actions.non_required`) : t(`actions.required`),
      icon: StarIcon,
      action: () => handleRequiredChange(field),
    },
    {
      label: t(`actions.delete`),
      icon: DeleteIcon,
      color: '#EF476F',
      action: () => handleDeleteClick(field),
    },
  ];

  const handleAddSuccess = () => {
    setShowModule(false);
    showSnackbar(t(`confirmation.configAdd`), 'success');
    
    if (filters.linkedTo && filters.linkedTo.trim() !== '') {
      fetchFields(filters.linkedTo.toLowerCase());
    }
  };

  const handleAddTypeSuccess = () => {
    setShowTypeForm(false);
    showSnackbar(t(`confirmation.configTypeAdd`), 'success');
    fetchTypes();
    
    if (filters.linkedTo && filters.linkedTo.trim() !== '') {
      fetchFields(filters.linkedTo.toLowerCase());
    }
  };


  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.config")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.config`)}</h1>
        <div className="ml-auto flex gap-3 text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_type`)}
            icon={AddIcon}
            onClick={() => handleAddTypeClick()}
          />
          <PrimaryButton
            label={t(`primaryButtons.add_field`)}
            icon={AddIcon}
            onClick={() => handleAddClick()}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2">
        {loading ? (
          <div className="col-span-full flex justify-center">
            <LoadingLogo />
          </div>
        ) : (
          filteredFields.map((field) => (
            <GenericCard
              key={field.id}
              title={
                <>
                  {field.name}
                  {field.mandatory && <span style={{ color: 'red' }}> *</span>}
                </>
              } subtitle={`Type du Champ - ${field.type?.name}`}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(field)} />
                </div>
              }
            />
          ))
        )}
      </div>

      {!loading && filteredFields.length === 0 && (
        <div className="text-center font-medium mt-4">
          <p>{t(`placeholder.config`)}</p>
        </div>
      )}

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          extra={true}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <FieldForm
          onSubmit={handleAddSuccess}
          onCancel={() => setShowModule(false)}
          linkedTo={filters.linkedTo}
        />
      )}

      {showTypeForm && (
        <TypeForm
          onSubmit={handleAddTypeSuccess}
          onCancel={() => setShowTypeForm(false)}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );

};

export default ExtraFields;
